/**
 * Skins picker UI. Flag-gated (hidden when FEATURES.skins is off). Owned skins
 * are selectable; premium skins not yet owned attempt a purchase through the
 * billing boundary (itself stubbed/dormant in v1). Rebuilds from the catalog,
 * so adding skins is a catalog edit only.
 */
import type { BillingService } from '../services/payments/billingService';
import type { SkinsService } from '../services/skins/skinsService';
import { el } from './dom';

export class SkinsScreen {
  private readonly grid = el('skins-grid');
  private readonly skins: SkinsService;
  private readonly billing: BillingService;
  private readonly onChange: () => void;

  constructor(skins: SkinsService, billing: BillingService, onChange: () => void) {
    this.skins = skins;
    this.billing = billing;
    this.onChange = onChange;
  }

  async render(): Promise<void> {
    const owned = new Set(await this.skins.ownedIds());
    const selected = this.skins.selectedId();
    this.grid.replaceChildren(
      ...this.skins.catalog().map((skin) => {
        const isOwned = owned.has(skin.id);
        const cell = document.createElement('div');
        cell.className = 'skin-cell';
        if (skin.id === selected) cell.classList.add('selected');
        if (!isOwned) cell.classList.add('locked');

        const swatch = document.createElement('div');
        swatch.className = 'skin-swatch';
        swatch.style.background = skin.color;

        const name = document.createElement('div');
        name.className = 'skin-name';
        name.textContent = skin.name;

        const tag = document.createElement('div');
        tag.className = 'skin-tag';
        tag.textContent = isOwned ? (skin.id === selected ? 'Equipped' : 'Owned') : 'Premium';

        cell.append(swatch, name, tag);
        cell.addEventListener('click', () => this.onCellClick(skin.id, isOwned));
        return cell;
      }),
    );
  }

  private async onCellClick(skinId: string, isOwned: boolean): Promise<void> {
    if (isOwned) {
      this.skins.select(skinId);
      this.onChange();
      await this.render();
      return;
    }
    // Premium + not owned: route through billing (no-op while payments dormant).
    const result = await this.billing.purchase(skinId);
    if (result.ok) {
      this.skins.select(skinId);
      this.onChange();
    }
    await this.render();
  }
}
